import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Modal, Table } from 'react-bootstrap';
import { FaLink, FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { getUrlsByClientId, createClientUrl, updateClientUrl, deleteClientUrl } from '../../../api/CLIENTS/clients';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

const LiensTab = ({ client }) => {
  const { hasPermission } = useAuth();
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);
  const [formData, setFormData] = useState({ url: '', label: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState(null);
  const isActive = Number(client.is_active) === 1;

  // Fetch URLs
  const fetchUrls = useCallback(async () => {
    if (!client?.id) return;

    setLoading(true);
    try {
      const response = await getUrlsByClientId(client.id);
      const urlsData = response?.data  || [];
      setUrls(Array.isArray(urlsData) ? urlsData : []);
    } catch (error) {
      console.error('Erreur lors de la récupération des URLs:', error);
      setUrls([]);
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    fetchUrls();
  }, [fetchUrls]);

  // Open modal for create/edit
  const handleOpenModal = (url = null) => {
    if (url) {
      setEditingUrl(url);
      setFormData({ url: url.url || '', label: url.label || '' });
    } else {
      setEditingUrl(null);
      setFormData({ url: '', label: '' });
    }
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUrl(null);
    setFormData({ url: '', label: '' });
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.url.trim()) {
      toast.error('L\'URL est obligatoire');
      return;
    }

    try {
      if (editingUrl) {
        // Update existing URL
        await updateClientUrl(client.id, editingUrl.id, { url: formData.url, label: formData.label });
        toast.success('Lien modifié avec succès');
      } else {
        // Create new URL
        await createClientUrl(client.id, { url: formData.url, label: formData.label });
        toast.success('Lien ajouté avec succès');
      }
      handleCloseModal();
      fetchUrls();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  // Delete URL with confirmation
  const handleDeleteClick = (url) => {
    setUrlToDelete(url);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!urlToDelete) return;

    try {
      await deleteClientUrl(client.id, urlToDelete.id);
      toast.success('Lien supprimé avec succès');
      setShowDeleteModal(false);
      setUrlToDelete(null);
      fetchUrls();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Row>
        <Col>
          <Card className="border-left-primary shadow mt-3">
            <Card.Body>
              <div className="text-center py-4">Chargement...</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <Card className="border-left-primary shadow mt-3">
            <Card.Header className="d-flex flex-row align-items-center justify-content-between">
              <h6 className="text-primary fw-semibold m-0">Liens utiles</h6>
              {hasPermission('EDIT_CLIENT') && (
                <button 
                  className="btn btn-primary btn-icon-split"
                  disabled={!isActive}
                  onClick={() => handleOpenModal()}
                >
                  <span
                    className="icon text-white-50"
                    style={{ alignItems: "center", justifyContent: "center" }}
                  >
                    <FaPlus style={{ color: "#fff" }} />
                  </span>
                  <span className="text">Ajouter un lien</span>
                </button>
              )}
            </Card.Header>
            <Card.Body>
              {urls.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">Aucun lien enregistré</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>URL</th>
                      <th style={{ width: '100px' }} className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urls.map((urlItem) => (
                      <tr key={urlItem.id}>
                        <td>
                          {urlItem.label || <span className="text-muted">Sans label</span>}
                        </td>
                        <td>
                          <a 
                            href={urlItem.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="d-flex align-items-center"
                          >
                            <FaLink className="me-2" />
                            {urlItem.url}
                          </a>
                        </td>
                        <td className="text-center">
                          {hasPermission('EDIT_CLIENT') && (
                            <>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-primary p-1"
                                disabled={!isActive}
                                onClick={() => handleOpenModal(urlItem)}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-1"
                                disabled={!isActive}
                                onClick={() => handleDeleteClick(urlItem)}
                              >
                                <FaTrashAlt />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingUrl ? 'Modifier le lien' : 'Ajouter un lien'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formLabel">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                name="label"
                placeholder="Ex: Site web, Documentation, etc."
                value={formData.label}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formUrl">
              <Form.Label>URL <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="url"
                name="url"
                placeholder="https://exemple.com"
                value={formData.url}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {editingUrl ? 'Modifier' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir supprimer ce lien ?</p>
          {urlToDelete && (
            <div className="alert alert-warning mb-0">
              <strong>{urlToDelete.label || 'Sans label'}</strong>
              <br />
              <small className="text-muted">{urlToDelete.url}</small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LiensTab;